import type { NavBadgeValue } from '@workspace/seedforge-ui/components/app-sidebar'
import { useEffect, useSyncExternalStore } from 'react'
import { useOverviewQuery } from '../../../hooks/api'

/**
 * "You have feedback you have not read yet", as a sidebar dot.
 *
 * Keyed on the evaluation's id rather than a boolean, so a re-run raises the
 * dot again: a second prescreen is new feedback the founder has not seen, and a
 * one-shot "dismissed" flag would swallow it. Device-local on purpose — this is
 * a nudge, not a record, and the cost of the founder seeing it once per browser
 * is far below the cost of a schema change and a write on every page view.
 *
 * The layout reads the badge and the Feedback page clears it, which are two
 * different components; localStorage alone does not re-render either of them,
 * so writes go through a tiny store both subscribe to.
 */
const seenKey = (startupId: string) => `sf-feedback-seen:${startupId}`

const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  // Another tab clearing the dot should clear it here too.
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

function readSeen(startupId: string): string | null {
  try {
    return localStorage.getItem(seenKey(startupId))
  } catch {
    // Private mode or a blocked origin. No dot beats a crash.
    return null
  }
}

function writeSeen(startupId: string, evaluationId: string) {
  try {
    localStorage.setItem(seenKey(startupId), evaluationId)
  } catch {
    return
  }
  for (const listener of listeners) listener()
}

/** The evaluation id this device has already seen, or null. */
function useSeenEvaluationId(startupId: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => readSeen(startupId),
    // No localStorage on the server; nothing is unseen until we know otherwise.
    () => null
  )
}

/**
 * Amber dot for the Feedback nav, or undefined when there is nothing new.
 * Undefined rather than null so it can be spread straight into the badge map.
 */
export function useFeedbackNavBadge(
  startupId: string,
  options?: { enabled?: boolean }
): NavBadgeValue | undefined {
  const overviewQuery = useOverviewQuery(startupId)
  const seenId = useSeenEvaluationId(startupId)

  if (options?.enabled === false) return undefined

  const data = overviewQuery.data?.status === 'ok' ? overviewQuery.data.data : null
  const evaluationId = (data as { evaluationId?: string | null } | null)?.evaluationId
  // Only finished feedback is worth walking to. A processing evaluation has
  // its own banner on the page itself.
  if (!evaluationId || data?.evaluationStatus !== 'completed') return undefined
  if (seenId === evaluationId) return undefined

  return { tone: 'amber', title: 'Review your feedback' }
}

/**
 * Clears the dot for whatever evaluation is on screen. Call it from the
 * Feedback page: arriving there is what "seen" means, and it covers every route
 * in — the sidebar, the Home card, the email link.
 */
export function useMarkFeedbackSeen(startupId: string, evaluationId: string | null | undefined) {
  useEffect(() => {
    if (evaluationId) writeSeen(startupId, evaluationId)
  }, [startupId, evaluationId])
}
